import { Box, Divider, Stack, Typography } from "@mui/material";
import { useRecordContext, useResourceContext } from "react-admin";
import { ResourceName, Reimbursement } from "@/types";
import { formatUSD } from "@/utils/formatters/currencyFormatter";
import { formatLongDate } from "@/utils/formatters/dateFormatter";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import InPlaceDollarEditor from "@/components/common/fields/InPlaceDollarEditor";
import InPlaceTextEditor from "@/components/common/fields/InPlaceTextEditor";
import InPlaceDateEditor from "@/components/common/fields/InPlaceDateEditor";
import InPlaceBooleanEditor from "@/components/common/fields/InPlaceBooleanEditor";
import { getProps } from "@/utils/helpers/columnsHelper";
import { loadKeyLockReason } from "../utils/loadKeyLock";

const safeUSD = (n: unknown) => (typeof n === "number" && !Number.isNaN(n) ? formatUSD(n) : "—");

/** Simple label/value row */
const StatRow = ({ label, value, color }: { label: string; value: string; color?: string }) => (
  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 0.75 }}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={500} color={color || "text.primary"}>
      {value}
    </Typography>
  </Stack>
);

/** Detail line for reason/date (shown below the amount row) */
const DetailLine = ({ reason, date }: { reason?: string; date?: string }) => {
  if (!reason && !date) return null;
  return (
    <Stack direction="row" justifyContent="space-between" sx={{ pl: 1, pb: 0.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic", maxWidth: "70%" }} noWrap>
        {reason || ""}
      </Typography>
      {date && (
        <Typography variant="caption" color="text.disabled">
          {formatLongDate(date)}
        </Typography>
      )}
    </Stack>
  );
};

/**
 * Financial breakdown for reimbursements.
 * Clean, scannable layout pairing amounts with their related reasons/dates.
 * Supports inline editing when user has edit permissions.
 */
export const FinancialBreakdown = () => {
  const record = useRecordContext<Reimbursement>();
  const resource = useResourceContext();
  const { canEdit } = useResourcePermissions(ResourceName.REIMBURSEMENTS);
  const res = resource || ResourceName.REIMBURSEMENTS;

  if (!record) return null;

  const getLabel = (source: string) => getProps(source, ResourceName.REIMBURSEMENTS)?.label || source;
  // Re-upload match-key fields are read-only on a file-loaded row (#7560).
  const lock = (source: string) => {
    const readOnlyReason = loadKeyLockReason(record, source);
    return { readOnly: !!readOnlyReason, readOnlyReason };
  };

  if (canEdit) {
    return (
      <Stack spacing={1}>
        <Typography variant="overline" color="text.secondary" sx={{ fontSize: "0.65rem", letterSpacing: 1 }}>
          Amounts
        </Typography>
        <InPlaceDollarEditor
          source="requested_amount"
          label={getLabel("requested_amount")}
          resource={res}
          showButtons
        />
        <InPlaceDollarEditor
          source="reimbursement_amount"
          label={getLabel("reimbursement_amount")}
          resource={res}
          showButtons
          {...lock("reimbursement_amount")}
        />
        <Divider sx={{ my: 0.5 }} />
        <InPlaceDollarEditor
          source="pended_amount"
          label={getLabel("pended_amount")}
          resource={res}
          showButtons
          {...lock("pended_amount")}
        />
        <InPlaceTextEditor source="pending_reason" label={getLabel("pending_reason")} resource={res} showButtons />
        <InPlaceDateEditor
          source="pended_audit_date"
          label={getLabel("pended_audit_date")}
          resource={res}
          showButtons
        />
        <Divider sx={{ my: 0.5 }} />
        <InPlaceDollarEditor
          source="adjustment_amount"
          label={getLabel("adjustment_amount")}
          resource={res}
          showButtons
        />
        <InPlaceTextEditor
          source="adjustment_reason"
          label={getLabel("adjustment_reason")}
          resource={res}
          showButtons
        />
        <InPlaceDateEditor
          source="adjustment_audit_date"
          label={getLabel("adjustment_audit_date")}
          resource={res}
          showButtons
        />
        <Divider sx={{ my: 0.5 }} />
        <InPlaceDollarEditor source="denial_amount" label={getLabel("denial_amount")} resource={res} showButtons />
        <InPlaceTextEditor source="denial_reason" label={getLabel("denial_reason")} resource={res} showButtons />
        <Divider sx={{ my: 1 }} />
        <Typography variant="overline" color="text.secondary" sx={{ fontSize: "0.65rem", letterSpacing: 1 }}>
          Payment
        </Typography>
        {/* Payment Date is the date the Claims Summary reports under (#7560). Document loads write
            only this field, so it must be editable here or a loaded row can't be re-dated. */}
        <InPlaceDateEditor source="payment_date" label={getLabel("payment_date")} resource={res} showButtons />
        <InPlaceTextEditor
          source="check_number"
          label={getLabel("check_number")}
          resource={res}
          showButtons
          {...lock("check_number")}
        />
        <InPlaceDateEditor source="check_date" label={getLabel("check_date")} resource={res} showButtons />
        <InPlaceBooleanEditor source="eft" label={getLabel("eft")} resource={res} showButtons />
      </Stack>
    );
  }

  // Read-only view — clean stat rows
  return (
    <Box>
      {/* Primary amounts */}
      <StatRow label="Requested" value={safeUSD(record.requested_amount)} color="primary.main" />
      <Divider />
      <StatRow label="Reimbursed" value={safeUSD(record.reimbursement_amount)} color="success.main" />
      <Divider />

      {/* Pended */}
      {(record.pended_amount || record.pending_reason) && (
        <>
          <StatRow label="Pended" value={safeUSD(record.pended_amount)} color="warning.main" />
          <DetailLine reason={record.pending_reason} date={record.pended_audit_date} />
          <Divider />
        </>
      )}

      {/* Adjusted */}
      {(record.adjustment_amount || record.adjustment_reason) && (
        <>
          <StatRow label="Adjusted" value={safeUSD(record.adjustment_amount)} color="info.main" />
          <DetailLine reason={record.adjustment_reason} date={record.adjustment_audit_date} />
          <Divider />
        </>
      )}

      {/* Denied */}
      {(record.denial_amount || record.denial_reason) && (
        <>
          <StatRow label="Denied" value={safeUSD(record.denial_amount)} color="error.main" />
          <DetailLine reason={record.denial_reason} />
          <Divider />
        </>
      )}

      {/* Payment */}
      {(record.payment_date || record.check_number || record.check_date || record.eft) && (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="overline" color="text.secondary" sx={{ fontSize: "0.65rem", letterSpacing: 1 }}>
            Payment
          </Typography>
          <Stack direction="row" spacing={3} sx={{ mt: 0.5 }}>
            {record.payment_date && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Payment Date
                </Typography>
                <Typography variant="body2">{formatLongDate(record.payment_date)}</Typography>
              </Box>
            )}
            {record.check_number && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Check #
                </Typography>
                <Typography variant="body2">{record.check_number}</Typography>
              </Box>
            )}
            {record.check_date && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Check Date
                </Typography>
                <Typography variant="body2">{formatLongDate(record.check_date)}</Typography>
              </Box>
            )}
            {record.eft !== undefined && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  EFT
                </Typography>
                <Typography variant="body2">{record.eft ? "Yes" : "No"}</Typography>
              </Box>
            )}
          </Stack>
        </Box>
      )}
    </Box>
  );
};
